import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { CarModel } from '../api/carModelsApi';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import TextArea from '@/components/form/input/TextArea';
import Button from '@/components/ui/button/Button';
import { useTechProfiles } from '../hooks/useCarModels';
import { findTechProfileIdByBodyType } from '../utils/techProfileMatching';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useToast } from '@/components/ui/toast/Toast';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useTranslation } from 'react-i18next';

const BODY_TYPE_OPTIONS = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'coupe', label: 'Coupe' },
  { value: 'suv', label: 'SUV' },
  { value: 'electric', label: 'Electric' }
];

const TRANSMISSION_TYPE_OPTIONS = [
  { value: 'auto', label: 'Automatic' },
  { value: 'manual', label: 'Manual' }
];

interface EditCarModelDto {
  slug: string;
  brand_name: string;
  model_name: string;
  transmission_type: string;
  body_type: string;
  tech_profile: string;
  description_en: string;
  description_fr: string;
  is_active: boolean;
  image_url?: string;
}

interface FormErrors {
  slug?: string;
  brand_name?: string;
  model_name?: string;
  transmission_type?: string;
  body_type?: string;
}

interface CarModelEditFormProps {
  onSubmit: (data: EditCarModelDto, image?: File) => Promise<void>;
  onClose: (hasUnsavedChanges?: boolean) => void;
  initialData: EditCarModelDto;
  currentData?: EditCarModelDto;
  onDataChange?: (data: EditCarModelDto) => void;
}

const CarModelEditForm = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarModelEditFormProps>(
  ({ onSubmit, onClose, initialData, onDataChange }, ref) => {
    const { t } = useTranslation();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { data: techProfilesResponse } = useTechProfiles();
    const techProfiles = techProfilesResponse?.data || [];
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [formData, setFormData] = useState<EditCarModelDto>(initialData);
    const [showOriginalImage, setShowOriginalImage] = useState(true);

    useEffect(() => {
      onDataChange?.(formData);
    }, [formData, onDataChange]);

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {};

      if (!formData.slug.trim()) {
        newErrors.slug = t('car_models.edit.errors.slug_required');
      }
      if (!formData.brand_name.trim()) {
        newErrors.brand_name = t('car_models.edit.errors.brand_name_required');
      }
      if (!formData.model_name.trim()) {
        newErrors.model_name = t('car_models.edit.errors.model_name_required');
      }
      if (!formData.transmission_type) {
        newErrors.transmission_type = t('car_models.edit.errors.transmission_type_required');
      }
      if (!formData.body_type) {
        newErrors.body_type = t('car_models.edit.errors.body_type_required');
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const clearFieldError = (field: keyof FormErrors) => {
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

    const generateSlug = (brandName: string, modelName: string) => {
      return `${brandName}-${modelName}`
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, slug: value }));
      if (value.trim()) {
        clearFieldError('slug');
      }
    };

    const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({
        ...prev,
        brand_name: value,
        // Only update slug if it's empty or matches the previous auto-generated pattern
        ...(prev.slug === '' || prev.slug === generateSlug(prev.brand_name, prev.model_name)
          ? { slug: generateSlug(value, prev.model_name) }
          : {})
      }));
      if (value.trim()) {
        clearFieldError('brand_name');
      }
    };

    const handleModelNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({
        ...prev,
        model_name: value,
        // Only update slug if it's empty or matches the previous auto-generated pattern
        ...(prev.slug === '' || prev.slug === generateSlug(prev.brand_name, prev.model_name)
          ? { slug: generateSlug(prev.brand_name, value) }
          : {})
      }));
      if (value.trim()) {
        clearFieldError('model_name');
      }
    };

    const handleTransmissionTypeChange = (value: string) => {
      setFormData(prev => ({ ...prev, transmission_type: value }));
      if (value) {
        clearFieldError('transmission_type');
      }
    };

    const handleBodyTypeChange = (value: string) => {
      setFormData(prev => ({ ...prev, body_type: value }));
      if (value) {
        clearFieldError('body_type');
      }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedImage(file);
        setShowOriginalImage(true);
      }
    };

    const handleImageClick = () => {
      fileInputRef.current?.click();
    };

    const handleRemoveImage = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedImage(null);
      setShowOriginalImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset the file input value
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      try {
        const selectedBodyTypeOption = BODY_TYPE_OPTIONS.find((option) => option.value === formData.body_type);
        const techProfileId = findTechProfileIdByBodyType(
          formData.body_type,
          techProfiles,
          selectedBodyTypeOption?.label
        );

        await onSubmit({
          ...formData,
          tech_profile: techProfileId ?? formData.tech_profile,
        }, selectedImage || undefined);
        toast.success(t('car_models.edit.success'));
      } catch (error) {
        toast.error(t('car_models.edit.error'));
      }
    };

    let imageUrl = undefined;

    if (showOriginalImage && initialData.image_url) {
      imageUrl = initialData.image_url;
    }

    if (selectedImage) {
      imageUrl = URL.createObjectURL(selectedImage);
    }

    return (
      <>
        <div className="px-6 py-4 border-b border-border-primary">
          <h5 className="text-xl font-semibold text-text-primary lg:text-2xl">
            {t('car_models.edit.title')}
          </h5>
          <p className="text-sm text-text-tertiary">
            {t('car_models.edit.description')}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="col-span-2">
              <Label>{t('car_models.edit.fields.image')}</Label>
              <div className="mt-2 flex items-center gap-4">
                <div
                  className="relative w-32 h-32 border-[1px] border-gray-300 rounded-lg overflow-hidden cursor-pointer"
                  onClick={handleImageClick}
                >
                  <div className="w-full h-full">
                    {imageUrl !== undefined ? (
                      <div
                        className="w-full h-full flex"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ImagePreview imageUrl={imageUrl}>
                          <img
                            src={imageUrl}
                            alt={t('car_models.edit.image_alt')}
                            className="w-full h-full object-cover"
                          />
                        </ImagePreview>
                      </div>
                    ) : (
                      <div className="relative w-32 h-32">
                        <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary bg-opacity-50 text-text-muted">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                    {showOriginalImage && (
                      <button
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 z-10 bg-bg-secondary rounded-full p-1 shadow-md hover:bg-bg-hover"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.edit.fields.slug')}</Label>
              <Input
                testId="car-model-form-field-slug"
                type="text"
                value={formData.slug}
                onChange={handleSlugChange}
                placeholder={t('car_models.edit.placeholders.enter_slug')}
                error={errors.slug}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.edit.fields.brand_name')} *</Label>
              <Input
                testId="car-model-form-field-brand_name"
                type="text"
                value={formData.brand_name}
                onChange={handleBrandNameChange}
                placeholder={t('car_models.edit.placeholders.enter_brand_name')}
                error={errors.brand_name}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.edit.fields.model_name')} *</Label>
              <Input
                testId="car-model-form-field-model_name"
                type="text"
                value={formData.model_name}
                onChange={handleModelNameChange}
                placeholder={t('car_models.edit.placeholders.enter_model_name')}
                error={errors.model_name}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.edit.fields.transmission_type')} *</Label>
              <Select
                testId="car-model-form-field-transmission_type"
                options={TRANSMISSION_TYPE_OPTIONS}
                onChange={handleTransmissionTypeChange}
                defaultValue={formData.transmission_type}
                placeholder={t('car_models.edit.placeholders.select_transmission')}
                error={errors.transmission_type}
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('car_models.edit.fields.body_type')} *</Label>
              <Select
                testId="car-model-form-field-body_type"
                options={BODY_TYPE_OPTIONS}
                onChange={handleBodyTypeChange}
                defaultValue={formData.body_type}
                placeholder={t('car_models.edit.placeholders.select_body_type')}
                error={errors.body_type}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.edit.fields.description_en')}</Label>
              <TextArea
                testId="car-model-form-field-description_en"
                value={formData.description_en}
                onChange={(value) => setFormData(prev => ({ ...prev, description_en: value }))}
                placeholder={t('car_models.edit.placeholders.enter_description_en')}
                rows={4}
              />
            </div>

            <div className="col-span-2">
              <Label>{t('car_models.edit.fields.description_fr')}</Label>
              <TextArea
                testId="car-model-form-field-description_fr"
                value={formData.description_fr}
                onChange={(value) => setFormData(prev => ({ ...prev, description_fr: value }))}
                placeholder={t('car_models.edit.placeholders.enter_description_fr')}
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border-primary">
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onClose()}
            >
              {t('car_models.edit.buttons.cancel')}
            </Button>
            <Button
              testId="car-model-form-submit-button"
              variant="primary"
              onClick={handleSubmit}
              disabled={Object.keys(errors).length > 0}
            >
              {t('car_models.edit.buttons.update')}
            </Button>
          </div>
        </div>
      </>
    );
  }
);

CarModelEditForm.displayName = 'CarModelEditForm';

const ProtectedCarModelEditForm = withFormProtection(CarModelEditForm);

interface CarModelEditFormWrapperProps extends Omit<CarModelEditFormProps, 'currentData' | 'onDataChange'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const CarModelEditFormWrapper = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarModelEditFormWrapperProps>((props, ref) => {
  const [currentData, setCurrentData] = useState<EditCarModelDto>(props.initialData);

  const handleDataChange = (data: EditCarModelDto) => {
    setCurrentData(data);
  };

  return (
    <ProtectedCarModelEditForm
      {...props}
      ref={ref}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

CarModelEditFormWrapper.displayName = 'CarModelEditFormWrapper';

export default CarModelEditFormWrapper;
